import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import Vapi from "@vapi-ai/web";
import Button from "./components/base/Button";
import ActiveCallDetail from "./components/ActiveCallDetail";
import { VAPI_PUBLIC_KEY } from "./constants";
import { isPublicKeyMissingError } from "./utils";

// Crear instancia de Vapi con la misma Public API Key para todos los *squads*
const vapi = new Vapi(VAPI_PUBLIC_KEY);

// Hook para manejar si la clave pública está inválida
const usePublicKeyInvalid = () => {
  const [showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage] = useState(false);

  useEffect(() => {
    if (showPublicKeyInvalidMessage) {
      setTimeout(() => {
        setShowPublicKeyInvalidMessage(false);
      }, 3000);
    }
  }, [showPublicKeyInvalidMessage]);

  return {
    showPublicKeyInvalidMessage,
    setShowPublicKeyInvalidMessage,
  };
};

// Componente para mostrar mensaje de error de clave pública
const PleaseSetYourPublicKeyMessage = () => {
  return (
    <div className="public-key-message">
      ¿Está faltando tu Public Key de Vapi? (revisa tu código)
    </div>
  );
};

// Componente para el enlace a la documentación
const ReturnToDocsLink = () => {
  return (
    <a
      href="https://conexiscorp.com/agentes"
      target="_blank"
      rel="noopener noreferrer"
      className="return-to-docs-link"
    >
      regresar
    </a>
  );
};

// Función para seleccionar el *squad* según el subdominio
const getSquadData = () => {
  const hostname = window.location.hostname;
  
  if (hostname.includes("hospital")) {
    return {
      agentId: "fb07c921-a167-4d38-ab92-615b73084d41", // ID del agente de hospital
      logo: "/logo_conexis.png", // Mantener logo actual
      background: "/Fondo_hospital_vapi.jpeg" // Fondo específico para hospital
    };
  } else if (hostname.includes("iagrowup")) {
    return {
      agentId: "0d97a76c-1b31-4ea7-95f1-ccba8f39f8c3", // ID del agente de IAGrowUp
      logo: "/logo_conexis.png", // Mantener logo actual
      background: "/Fondo_IAGU_vapi.jpeg" // Fondo específico para IAGrowUp
    };
  } else if (hostname.includes("ventas")) {
    return {
      agentId: "a3c1a41f-1c42-4a68-9953-136a319b94b9", // ID del agente de ventas Camila
      logo: "/logo_conexis.png", // Mantener logo actual
      background: "/Fondo_ventas_vapi.jpeg" // Fondo específico para ventas
    };
  } else {
    return {
      agentId: "6e8685cf-9444-4ce3-bdad-b4f60a6bb73a", // ID del agente por defecto Yarbis
      logo: "/logo_conexis.png", // Mantener logo actual
      background: "/Fondo_Agencia_Conexis.png" // Fondo actual
    };
  }
};

const { agentId, logo, background } = getSquadData();

const App = () => {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [assistantIsSpeaking, setAssistantIsSpeaking] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const { showPublicKeyInvalidMessage, setShowPublicKeyInvalidMessage } = usePublicKeyInvalid();
  
  const logoRef = useRef(); // Referencia al logo para animarlo

  useEffect(() => {
    vapi.on("call-start", () => {
      setConnecting(false);
      setConnected(true);
      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("call-end", () => {
      setConnecting(false);
      setConnected(false);
      setShowPublicKeyInvalidMessage(false);
    });

    vapi.on("speech-start", () => setAssistantIsSpeaking(true));
    vapi.on("speech-end", () => setAssistantIsSpeaking(false));
    vapi.on("volume-level", (level) => setVolumeLevel(level));

    // Animar el logo según el volumen
    if (logoRef.current) {
      const scale = 1 + volumeLevel * 0.5;
      logoRef.current.style.transform = `scale(${scale})`;
    }

    vapi.on("error", (error) => {
      console.error("Evento: Error detectado", error);
      setConnecting(false);
      if (isPublicKeyMissingError({ vapiError: error })) {
        setShowPublicKeyInvalidMessage(true);
      }
    });
  }, [volumeLevel, setShowPublicKeyInvalidMessage]);

  const startCallInline = () => {
    setConnecting(true);
    vapi.start(agentId).catch(() => setConnecting(false)); // Usar el ID del agente adecuado
  };

  const endCall = () => {
    vapi.stop();
  };

  return (
    <div className="app-container" style={{ backgroundImage: `url(${background})` }}>
      <div className="audio-visualizer">
        <img ref={logoRef} src={logo} alt="Logo del Squad" className="vibrating-logo" />
      </div>
      {!connected ? (
        <Button
          label="Hablar con el asistente"
          onClick={startCallInline}
          isLoading={connecting}
        />
      ) : (
        <ActiveCallDetail
          assistantIsSpeaking={assistantIsSpeaking}
          volumeLevel={volumeLevel}
          onEndCallClick={endCall}
        />
      )}
      {showPublicKeyInvalidMessage && <PleaseSetYourPublicKeyMessage />}
      <ReturnToDocsLink />
    </div>
  );
};

export default App;
